// @flow

// for various window.requires
/* eslint-disable no-restricted-globals */

import { featureIsEnabled } from '@ta/common.features';
import trackEventAJAX, {
  type TrackingEventAJAXProps,
  trackEventAJAXOnPageOnce,
} from '@ta/common.tracking';
import Logging from '@ta/common.logging';

/** DoubleClick domain used when listening to messages (e.g. Dynamic Ad data requests, or when an ad wants to expand etc.) */
export const DBL_CLICK_DOMAIN = 'https://tpc.googlesyndication.com';

/** COLLAPSER ID returned by DoubleClick when there is nothing to display */
export const COLLAPSER_ADVERTISER_ID = 55978451;
export const AD_PRF_TRACK_MODULE = 'ads_perf';

type CpmParams = {|
  gptBase: any,
  adStubs: any,
  gptConfig: any,
  ppid: any,
  pageTargeting: any,
  adTrackingConfig: any,
  deferDisabled?: ?boolean,
|};

export type CpmMobileParams = {|
  ...CpmParams,
|};

export type CpmDesktopParams = {|
  ...CpmParams,
  enableScrollUpInjection?: ?boolean,
|};

const prfMark = (lbl: any) => {
  const perf = window.performance;
  if (perf && perf.mark) {
    perf.mark(lbl);
  }
};

function prfMeasure(name: string, startMark: string, endMark: string) {
  const perf = window.performance;
  if (perf && perf.measure) {
    perf.measure(name, startMark, endMark);
    const entry = perf.getEntriesByName(name)[0];
    if (entry) {
      return entry.duration;
    }
  }
  return null;
}

export function trackAdPerformance(name: string, condition: boolean, trackOnce: boolean = false) {
  const startMark = `${name}_start`;
  const endMark = `${name}_end`;
  if (!condition) {
    prfMark(startMark);
  } else {
    prfMark(endMark);
    const latency = prfMeasure(name, startMark, endMark);
    if (latency) {
      Logging.debug(`[ADS] ${name}: ${latency}`);
      const trackingData: TrackingEventAJAXProps = {
        module: AD_PRF_TRACK_MODULE,
        action: name,
        value: latency,
      };
      if (trackOnce) {
        trackEventAJAXOnPageOnce(trackingData);
      } else {
        trackEventAJAX(trackingData);
      }
    }
  }
}

export const listenToCpmMessage = (cpmMessageType: string, callback: any => void) => {
  const isDebug = location.hash === '#tearsheet';
  const domain = isDebug ? `https://${location.hostname}` : DBL_CLICK_DOMAIN;
  window.addEventListener('message', event => {
    if (event.origin === domain && event.data && event.data.cpmMessageType === cpmMessageType) {
      callback(event.data);
    }
  });
};

const eventBelongsToSlot = (adSlotId: string, event: any) => {
  // google pub tag event has dom ID of the slot the event relates to
  const domId = event && event.slot ? event.slot.getSlotElementId() : null;
  return domId === adSlotId;
};

export const isEmptyOrCollapser = (event: any) =>
  event.isEmpty || event.advertiserId === COLLAPSER_ADVERTISER_ID;

/**
 * Register an event listener to an ad slot w/ a wrapper that listens for slotRenderEnded.
 * (see https://developers.google.com/doubleclick-gpt/reference#googletag.events.slotrenderendedevent)
 * (also see @ta/cpm.ad-target-mobile-adhesion for usage)
 *
 * @param adSlotId: id attribute of the element that ad will be injected into
 * @param wrapperRef: element reference to the ad, or the ad's wrapper element
 * @param callback
 */
export const registerRenderEventListenerWithWrapper = (
  adSlotId: string,
  wrapperRef: any,
  callback: any => void,
) => {
  if (typeof window !== 'undefined') {
    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd.push(() => {
      window.googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
        if (
          eventBelongsToSlot(adSlotId, event) &&
          !isEmptyOrCollapser(event) &&
          wrapperRef.current &&
          wrapperRef.current.classList
        ) {
          // Note: There is no pubads().removeEventListener so we'll work around via a classList check
          const wrapperLoadedClass = `${adSlotId}-loaded`;
          if (!wrapperRef.current.classList.contains(wrapperLoadedClass)) {
            wrapperRef.current.classList.add(wrapperLoadedClass);
            callback(event);
          }
        }
      });
    });
  } else {
    Logging.error(
      'The function registerRenderEventListenerWithWrapper should not be called during SSR',
    );
  }
};

/**
 * Register an event listener to an ad slot that listens for slotRenderEnded.
 * (see https://developers.google.com/doubleclick-gpt/reference#googletag.events.slotrenderendedevent)
 * (also see @ta/cpm.ad-target-inline-external for usage)
 *
 * @param adSlotId: id attribute of the element that ad will be injected into
 * @param callback: takes event as input, should handle collapser and non-collapser events
 */
export const registerRenderEventListener = (adSlotId: string, callback: any => void) => {
  if (typeof window !== 'undefined') {
    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd.push(() => {
      window.googletag.pubads().addEventListener('slotRenderEnded', (event: any) => {
        if (eventBelongsToSlot(adSlotId, event)) {
          callback(event);
        }
      });
    });
  } else {
    Logging.error('The function registerRenderEventListener should not be called during SSR');
  }
};

export const enableCpmContent = (
  params: CpmDesktopParams,
  modalServlet: ?string,
  onLoad?: () => void,
) => {
  if (SERVER || !params || !params.gptBase || featureIsEnabled('hr_disable_cpm_ads')) {
    return;
  }
  window.require(['ta/Core/TA.Store'], taStore => {
    const toStore: Object = {
      'ads.gptBase': params.gptBase,
      'ads.adStubs': params.adStubs,
      'ads.gptConfig': params.gptConfig,
      'ads.ppid': params.ppid,
      'ads.pageTargeting': params.pageTargeting,
      'ads.adTracking': params.adTrackingConfig,
      'ads.enableScrollUpInjection': !!params.enableScrollUpInjection,
      'ads.deferDisabled': params.deferDisabled,
    };

    if (modalServlet) {
      toStore[`ads.${modalServlet}.adStubs`] = params.adStubs;
      toStore[`ads.${modalServlet}.pageTargeting`] = params.pageTargeting;
    }

    if (featureIsEnabled('timed_refresh_after_15s')) {
      toStore['ads.timed_refresh_min'] = 15000;
    }

    Object.keys(toStore).forEach(k => {
      const v = toStore[k];
      taStore.store(k, v);
    });

    prfMark('start_load_ads_js');

    window.require(['trjs!cpm/DesktopAds'], desktopAds => {
      desktopAds.initDoubleClick();
      if (onLoad) onLoad();
    });
  });
};

export const enableCpmContentMobile = (
  params: CpmMobileParams,
  modalServlet: ?string,
  onLoad?: () => void,
) => {
  if (SERVER || !params || !params.gptBase) {
    return;
  }

  if (modalServlet) {
    const { adStubs, pageTargeting } = params;
    window.taAdsJSON = {
      ...window.taAdsJSON,
      [modalServlet]: {
        adStubs,
        pageTargeting,
      },
    };
  }

  window.require(['trjs!cpm/MobileWeb'], mobileWeb => {
    mobileWeb.initDoubleClick(params);
    if (onLoad) onLoad();
  });
};

export const willLoadCpmContent = () => {
  if (SERVER) {
    return false;
  }
  let willLoad = false;
  window.require(['ta/Core/TA.Store'], taStore => {
    if (taStore.retrieve('ads.adStubs') === undefined) {
      willLoad = true;
    }
  });
  return willLoad;
};

export const enableCpmContentAPR = (params: any) => enableCpmContent(params);

export default {};
